import React, { useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AdBanner from '../../components/AdBanner';
import { useTrackLPView, trackLPClick } from '../../utils/lpTracking';
import futezapLogo from '../../assets/futezap.jpg';
import shotRanking from '../../assets/landing/ranking-tela.png';
import shotCardJogo from '../../assets/landing/card-jogo-fechado.png';
import shotStatsList from '../../assets/landing/stats-lista-jogadores.png';
import shotStatsPanel from '../../assets/landing/stats-painel-jogador.png';

const { Title, Text, Paragraph } = Typography;

const RANKING_MOCK = [
  { pos: 1, name: 'Marcos SAN', total: 14 },
  { pos: 2, name: 'Ronaldo', total: 12 },
  { pos: 3, name: 'Maicon', total: 10 },
  { pos: 4, name: 'Vitor Vale', total: 9 },
  { pos: 5, name: 'Teste', total: 8 },
];

function setMeta(name, content, attr = 'name') {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function useIsLogged() {
  const [logged, setLogged] = useState(false);
  useEffect(() => {
    try {
      const t = localStorage.getItem('token');
      if (!t) return;
      const p = JSON.parse(atob(t.split('.')[1]));
      if (p.exp * 1000 > Date.now()) setLogged(true);
    } catch { /* silencioso */ }
  }, []);
  return logged;
}

const LP_PATH = '/como-funciona-ranking';

const HowRankingWorks = () => {
  const navigate = useNavigate();
  const logged = useIsLogged();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useTrackLPView(LP_PATH);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Novidade: Ranking do time — Futezap';
    setMeta('description', 'Como usar o Ranking do time no Futezap: onde encontrar, como lançar as estatísticas e como enviar a lista direto no grupo do WhatsApp.');
    setMeta('og:title', 'Novidade no Futezap: Ranking do time', 'property');
    setMeta('og:description', 'Como funciona o novo ranking do time e o botão de enviar direto no grupo.', 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:image', `${window.location.origin}/futezap.jpg`, 'property');
    return () => { document.title = prev; };
  }, []);

  const openRanking = () => {
    trackLPClick(LP_PATH, logged ? 'abrir_meu_ranking_logado' : 'abrir_meu_ranking_deslogado');
    if (logged) navigate('/rankings');
    else window.location.href = '/?next=/rankings';
  };

  const shot = {
    width: '100%',
    maxWidth: 300,
    borderRadius: 14,
    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
    border: '1px solid #eee',
    display: 'block',
  };

  const section = ({ n, titulo, children, img, imgAlt }) => (
    <div style={{
      display: 'flex', flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? 16 : 32, alignItems: 'flex-start',
      padding: '28px 0', borderTop: '1px solid #eef1f4',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ color: '#8c8c8c', fontSize: 12, fontWeight: 600, letterSpacing: 0.5 }}>
          {n}
        </Text>
        <Title level={4} style={{ margin: '4px 0 8px' }}>{titulo}</Title>
        <div style={{ color: '#4a4a4a', fontSize: 15, lineHeight: 1.6 }}>
          {children}
        </div>
      </div>
      <div style={{ flex: '0 0 auto', alignSelf: isMobile ? 'center' : 'flex-start' }}>
        <img src={img} alt={imgAlt} style={shot} loading="lazy" />
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <AdBanner publicMode />
      <div style={{
        maxWidth: 760, margin: '0 auto',
        padding: isMobile ? '20px 12px 40px' : '32px 20px 64px',
      }}>

        {/* Cabecalho simples — logo + wordmark, sem hero */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <img src={futezapLogo} alt="Futezap" style={{ height: 28, width: 28, borderRadius: 5 }} />
          <Text strong style={{ color: '#128C7E', fontSize: 15 }}>Futezap</Text>
          <span style={{ color: '#c1c9d2', fontSize: 13 }}>·</span>
          <Text style={{ color: '#8c8c8c', fontSize: 13 }}>Novidades</Text>
        </div>

        {/* Post */}
        <div style={{
          background: '#fff', borderRadius: 14,
          padding: isMobile ? '24px 20px' : '40px 44px',
          border: '1px solid #eef1f4',
        }}>
          <Text style={{ color: '#8c8c8c', fontSize: 13 }}>05 de agosto de 2026</Text>
          <Title level={2} style={{ marginTop: 6, marginBottom: 16 }}>
            Chegou o Ranking do time
          </Title>

          <Paragraph style={{ color: '#4a4a4a', fontSize: 16, lineHeight: 1.65 }}>
            Fala, organizador. Liberamos essa semana o <strong>Ranking</strong> lá no menu do seu time.
            Ele soma artilharia, assistência, cartão amarelo, vermelho e falta do ano inteiro,
            a partir das estatísticas que você lança <strong>durante</strong> ou <strong>depois</strong> do jogo.
          </Paragraph>

          <Paragraph style={{ color: '#4a4a4a', fontSize: 16, lineHeight: 1.65, marginBottom: 24 }}>
            E o pulo do gato: tem um botão pra você <strong>publicar a lista direto no grupo do WhatsApp</strong>,
            no formato certo, sem precisar copiar e colar nada.
          </Paragraph>

          {/* Passos */}
          {section({
            n: 'ONDE ENCONTRAR',
            titulo: 'No menu lateral, item "Ranking"',
            img: shotRanking,
            imgAlt: 'Tela de ranking',
            children: (
              <>
                É a tela que abre ali. Tem aba pra cada tipo (Artilharia, Assistências, Amarelos, Vermelhos, Faltas)
                e filtro por ano no canto superior esquerdo.
              </>
            ),
          })}

          {section({
            n: 'PASSO 1',
            titulo: 'No card do jogo, aperta "Lançar estatísticas"',
            img: shotCardJogo,
            imgAlt: 'Card do jogo com botão Lançar estatísticas',
            children: (
              <>
                No menu <em>Jogos</em>, o botão azul <strong>Lançar estatísticas</strong> aparece nos
                jogos <strong>ABERTOS</strong> (pra você registrar gol e cartão em tempo real, na beira do campo)
                e também nos <strong>FECHADOS</strong> (pra fechar as stats calmamente depois).
                É por aí que você entra em qualquer um dos casos.
              </>
            ),
          })}

          {section({
            n: 'PASSO 2',
            titulo: 'Toca no jogador e vai no + / −',
            img: shotStatsList,
            imgAlt: 'Modal de estatísticas com lista de jogadores',
            children: (
              <>
                Abre a lista dos que jogaram. Toca no nome pra abrir o painel dele, aperta <strong>+</strong> pra somar
                gol/assist/cartão/falta, <strong>−</strong> se errou. Salva e volta pra escolher o próximo.
                Se preferir preencher tudo numa tabela só, tem o modo <em>Lote</em>.
              </>
            ),
          })}

          {section({
            n: 'DETALHE',
            titulo: 'Botões grandes, feito pra usar no celular na beira do campo',
            img: shotStatsPanel,
            imgAlt: 'Painel de +/- do jogador',
            children: (
              <>
                Cada estatística tem sua cor (verde gol, azul assist, amarelo/vermelho cartão, roxo falta).
                Sem digitação, sem tela cheia de campo pra preencher.
              </>
            ),
          })}

          {section({
            n: 'PASSO 3',
            titulo: 'Publica no grupo com o botão verde',
            img: shotRanking,
            imgAlt: 'Tela de ranking com botão Enviar no grupo',
            children: (
              <>
                Na tela do Ranking, escolhe a aba que quer publicar (ex: Artilharia), aperta <strong>Enviar no grupo</strong>,
                confere o preview e envia. Cai formatado bonito no zap do time.
                <br /><br />
                <Text style={{ color: '#8c8c8c', fontSize: 14 }}>
                  Detalhe: tem cooldown de 6h entre um envio e outro, pra não virar spam no grupo.
                </Text>
              </>
            ),
          })}

          {/* Preview whats */}
          <div style={{ borderTop: '1px solid #eef1f4', paddingTop: 28, marginTop: 8 }}>
            <Title level={5} style={{ margin: 0, color: '#4a4a4a' }}>Fica assim no grupo:</Title>
            <div style={{
              background: '#dcf8c6', borderRadius: 12, padding: 16,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
              fontSize: 14, marginTop: 12, maxWidth: 340,
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>🏆 ARTILHARIA 2026</div>
              {RANKING_MOCK.map(r => (
                <div key={r.pos} style={{ lineHeight: 1.7 }}>⚽ {r.total} - {r.name}</div>
              ))}
            </div>
          </div>

          {/* Rodape do post */}
          <div style={{ borderTop: '1px solid #eef1f4', paddingTop: 24, marginTop: 32 }}>
            <Paragraph style={{ color: '#4a4a4a', fontSize: 15, lineHeight: 1.65 }}>
              Se seu time já tem jogos com estatística lançada, o ranking já tá montado esperando.
              Se ainda não lançou, é começar pelo próximo jogo — leva 2 minutos e o placar da temporada
              vai se montando sozinho.
            </Paragraph>
            <Paragraph style={{ color: '#8c8c8c', fontSize: 14, marginBottom: 20 }}>
              Qualquer dúvida, responde aqui mesmo no zap com a gente.
            </Paragraph>

            <Button
              type="primary"
              icon={<TrophyOutlined />}
              onClick={openRanking}
              style={{
                background: '#128C7E', borderColor: '#128C7E',
                fontWeight: 600, height: 40, padding: '0 20px',
              }}
            >
              {logged ? 'Abrir meu ranking' : 'Entrar e ver meu ranking'}
            </Button>
          </div>
        </div>

        {/* Assinatura */}
        <div style={{ textAlign: 'center', color: '#8c8c8c', fontSize: 13, marginTop: 20 }}>
          — time Futezap
        </div>
      </div>
    </div>
  );
};

export default HowRankingWorks;
